import cn from "classnames";
import { type ReactNode, useRef, useState } from "react";
import {
  Button as AriaButton,
  DropIndicator,
  type DropTarget,
  GridList,
  GridListItem,
  useDragAndDrop,
} from "react-aria-components";
import { IconChevronDown, IconReorder } from "./icons";
import { CheckBox } from "./input";
import type { SelectOption } from "./select";

export interface ReorderableMultiSelectProps<V extends string = string> {
  options: SelectOption<V>[];
  selectedValues: V[];
  onSelectionChange: (values: V[]) => void;
  onOrderChange: (orderedValues: V[]) => void;
  noTriggerDisplayText?: boolean;
  getTriggerDisplayText?: (selectedValues: V[], totalCount: number) => string;
  disabled?: boolean;
  className?: string;
  dropdownClassName?: string;
  ariaLabel?: string;
  showSelectAllButton?: boolean;
  showClearButton?: boolean;
  chevronIcon?: ReactNode;
}

function renderDropIndicator(target: DropTarget) {
  return (
    <DropIndicator
      target={target}
      className={({ isDropTarget }) =>
        cn(
          "h-0.5 mx-2 rounded-full outline-none",
          isDropTarget ? "bg-indigo-500" : "bg-transparent",
        )
      }
    />
  );
}

function SelectAllClearButtons({
  showSelectAll,
  showClear,
  disabled,
  onSelectAll,
  onClear,
}: {
  showSelectAll: boolean;
  showClear: boolean;
  disabled: boolean;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const showMenu = showSelectAll || showClear;
  if (!showMenu) return null;

  return (
    <>
      <div
        className={cn(
          "flex items-center px-2 py-2",
          showSelectAll && showClear ? "justify-between" : "justify-end",
        )}
      >
        {showSelectAll ? (
          <button
            type="button"
            className={cn(
              "text-sm font-medium",
              disabled
                ? "text-indigo-200 cursor-not-allowed"
                : "text-indigo-400 hover:text-gray-600",
            )}
            onClick={onSelectAll}
            disabled={disabled}
          >
            Select All
          </button>
        ) : null}
        {showClear ? (
          <button
            type="button"
            className={cn(
              "text-sm font-medium",
              disabled
                ? "text-indigo-200 cursor-not-allowed"
                : "text-indigo-400 hover:text-indigo-600",
            )}
            onClick={onClear}
            disabled={disabled}
          >
            Clear
          </button>
        ) : null}
      </div>
      <div className="h-px bg-gray-200 my-2" />
    </>
  );
}

export function ReorderableMultiSelect<V extends string = string>({
  options,
  selectedValues,
  onSelectionChange,
  onOrderChange,
  noTriggerDisplayText = false,
  getTriggerDisplayText,
  disabled = false,
  className = "",
  dropdownClassName,
  ariaLabel,
  showSelectAllButton = true,
  showClearButton = true,
  chevronIcon,
}: ReorderableMultiSelectProps<V>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragActiveRef = useRef(false);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (dragActiveRef.current) return;
    const next = e.relatedTarget as Node | null;
    if (!containerRef.current) return;
    if (!next || !containerRef.current.contains(next)) {
      setOpen(false);
    }
  };

  const handleMouseDown = () => {
    containerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const selectedSet = new Set(selectedValues);

  const onToggleOne = (v: V) => {
    const next = new Set(selectedValues);
    if (next.has(v)) {
      next.delete(v);
    } else {
      next.add(v);
    }
    onSelectionChange(Array.from(next));
  };

  const onSelectAll = () => {
    const all = options.map((o) => o.value as V);
    onSelectionChange(all);
  };

  const onClear = () => {
    const none = options.filter((o) => o.disabled).map((o) => o.value);
    onSelectionChange(none);
  };

  const triggerText = noTriggerDisplayText
    ? ""
    : getTriggerDisplayText
      ? getTriggerDisplayText(selectedValues, options.length)
      : selectedValues.length === 0
        ? "Select"
        : selectedValues.length === 1
          ? (options.find((o) => o.value === selectedValues[0])?.label ??
            "1 selected")
          : `${selectedValues.length} selected`;

  const { dragAndDropHooks } = useDragAndDrop({
    getItems(keys) {
      return [...keys].map((key) => ({
        "text/plain": String(key),
      }));
    },
    onReorder(e) {
      const draggedKey = [...e.keys][0] as V;
      const currentOrder = options.map((o) => o.value as V);
      const fromIdx = currentOrder.indexOf(draggedKey);
      if (fromIdx < 0) return;

      const next = [...currentOrder];
      next.splice(fromIdx, 1);

      let targetKey: V;
      if (e.target.dropPosition === "before") {
        targetKey = e.target.key as V;
        const toIdx = next.indexOf(targetKey);
        next.splice(toIdx, 0, draggedKey);
      } else if (e.target.dropPosition === "after") {
        targetKey = e.target.key as V;
        const toIdx = next.indexOf(targetKey);
        next.splice(toIdx + 1, 0, draggedKey);
      }

      onOrderChange(next);
    },
    onDragStart() {
      dragActiveRef.current = true;
    },
    onDragEnd() {
      dragActiveRef.current = false;
    },
    renderDropIndicator,
  });

  const triggerClassName = cn(
    "relative",
    "text-black",
    "border",
    "hover:border-black-300 hover:bg-black-100",
    "focus:ring-gray-500 focus:border-gray-500",
    "active:text-black",
    "disabled:bg-black-50 disabled:border-black-100 disabled:text-black",
    "rounded-md shadow-sm",
    "px-2.5 py-1.5",
    noTriggerDisplayText || "pr-10",
    "w-full text-left",
    className,
  );

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      tabIndex={-1}
      onBlur={handleBlur}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
    >
      <button
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerClassName}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        {noTriggerDisplayText || (
          <span className="block truncate">{triggerText}</span>
        )}
        <span
          className={cn(
            noTriggerDisplayText || "absolute right-3 top-1/2 -translate-y-1/2",
            "pointer-events-none text-gray-700",
          )}
        >
          {chevronIcon || <IconChevronDown variant="sm" />}
        </span>
      </button>

      {open ? (
        <div
          className={cn(
            "absolute z-10 mt-1 min-w-full w-max",
            "bg-white border border-gray-200 rounded-md shadow-lg",
            "p-2",
            dropdownClassName,
          )}
        >
          <SelectAllClearButtons
            showSelectAll={showSelectAllButton}
            showClear={showClearButton}
            disabled={disabled}
            onSelectAll={onSelectAll}
            onClear={onClear}
          />

          <div className="overflow-auto py-1">
            <GridList
              aria-label="Reorderable column list"
              selectionMode="none"
              items={options.map((o) => ({
                id: o.value,
                label: o.label,
                value: o.value as V,
                disabled: o.disabled,
              }))}
              dragAndDropHooks={dragAndDropHooks}
              className="outline-none"
            >
              {(item) => {
                const checked = selectedSet.has(item.value as V);
                return (
                  <GridListItem
                    id={item.id}
                    textValue={item.label}
                    className={cn(
                      "flex items-center",
                      "rounded",
                      "my-0.5",
                      "pr-2",
                      "outline-none",
                      "focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-inset",
                      "hover:bg-gray-50",
                      "cursor-grab active:cursor-grabbing",
                      item.disabled ? "opacity-50" : "",
                    )}
                  >
                    <AriaButton
                      slot="drag"
                      aria-label={`Reorder ${item.label}`}
                      className={cn(
                        "p-1 rounded shrink-0 touch-none select-none",
                        "text-gray-300 hover:text-gray-500",
                        "bg-transparent border-none outline-none",
                      )}
                    >
                      <IconReorder aria-hidden="true" className="w-3.5 h-3.5" />
                    </AriaButton>

                    <button
                      type="button"
                      className={cn(
                        "flex items-center flex-1 min-w-0",
                        "px-1 py-1.5 text-left text-sm text-gray-700",
                        "focus:outline-none",
                        "focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-inset rounded",
                      )}
                      onClick={() => onToggleOne(item.value as V)}
                      disabled={item.disabled}
                    >
                      <div className="pointer-events-none">
                        <CheckBox
                          label={item.label}
                          checked={checked}
                          onChange={() => {}}
                          variant="default"
                          variantSize="sm"
                          disabled={item.disabled}
                          labelClassName="ml-2"
                        />
                      </div>
                    </button>
                  </GridListItem>
                );
              }}
            </GridList>
          </div>
        </div>
      ) : null}
    </div>
  );
}
